import { Button, Tooltip, IconName } from "@blueprintjs/core"
import { DateInput as BPDateInput } from "@blueprintjs/datetime"

/**
 * Convert a “YYYY-MM-DD” string to a Date at 00:00:00.000 in the user’s
 * local time zone.  If the string is invalid, today’s local-midnight is used.
 */
export function toLocalDate(dateStr: string = "") {
  const now = new Date()
  const parts = dateStr.split("-")

  let year: number, month: number, day: number

  if (parts.length === 3) {
    ;[year = now.getFullYear(), month = 1, day = 1] = parts.map(Number)

    // Build the candidate date.
    const candidate = new Date(year, month - 1, day, 0, 0, 0, 0)

    // Check it matches the requested Y-M-D (guards against 2026-02-31, etc.).
    if (
      candidate.getFullYear() === year &&
      candidate.getMonth() === month - 1 &&
      candidate.getDate() === day
    ) {
      return candidate // ✅ valid, return it
    }
  }

  // Anything invalid drops to "today at local-midnight".
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
}

function defaultFormattedDate(date: Date) {
  const year = date.getFullYear()
  const month = (1 + date.getMonth()).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")

  return month + "/" + day + "/" + year
}

type DateInputProps = {
  value: string | null
  onChange: (selectedDate: string | null) => void
  onClear?: () => void
  formatDate?: (d: Date) => string
  parseDate?: (d: string) => false | Date | null
  showLeftIcon?: boolean
  placeholder?: string
  removeBtnIcon?: IconName
  removeValueTooltipContent?: string
  isDisabled?: boolean
  isError?: boolean
  minDate?: string
  maxDate?: Date
}

export default function DateInput({
  value,
  onChange,
  onClear,
  formatDate = defaultFormattedDate,
  parseDate = (d: string) => new Date(d),
  showLeftIcon = false,
  placeholder = "",
  removeBtnIcon = "cross",
  removeValueTooltipContent,
  isDisabled = false,
  isError = false,
  minDate,
  maxDate,
}: DateInputProps) {
  const maximumDate = maxDate ? maxDate : new Date()
  if (!maxDate) {
    maximumDate.setFullYear(new Date().getFullYear() + 2)
  }

  const minimumDate = minDate ? toLocalDate(minDate) : new Date()

  // if no minDate prop then the minimum date should go back to year 1900
  if (!minDate) {
    minimumDate.setFullYear(1900)
  }

  return (
    <BPDateInput
      disabled={isDisabled}
      highlightCurrentDay={false}
      value={value}
      onChange={onChange}
      parseDate={parseDate}
      formatDate={formatDate}
      maxDate={maximumDate}
      minDate={minimumDate}
      inputProps={{
        intent: isError ? "danger" : "none",
        rightElement:
          (value || onClear) && !isDisabled ? (
            <Tooltip
              content={removeValueTooltipContent}
              position="top"
              disabled={!removeValueTooltipContent}
              hoverOpenDelay={800}
              className="!mt-0"
            >
              <Button
                minimal
                icon={removeBtnIcon}
                onClick={(evt) => {
                  evt.preventDefault()
                  evt.stopPropagation()
                  if (onClear) {
                    onClear()
                    return
                  }

                  if (onChange) {
                    onChange("")
                  }
                }}
              />
            </Tooltip>
          ) : undefined,
        leftIcon: showLeftIcon ? "calendar" : undefined,
        placeholder,
        // @ts-ignore - this still works even with ts error
        "data-1p-ignore": true,
      }}
      popoverProps={{
        usePortal: false,
        captureDismiss: true,
      }}
    />
  )
}
